import { UserRoundX } from '@/lib/icons';
import { ReportPageLayout } from '@/components/reports';
import { reportsService } from '@/services/reports-service';
import type { ColumnDef, FilterConfig, ReportFilters } from '@/types/reports';

interface UserProduct {
  id: string;
  name: string;
  expiration_date?: string;
}

interface User {
  id: string;
  attributes: {
    first_name: string;
    last_name: string;
    email: string;
    account_name?: string;
    affiliate_name?: string;
    products?: UserProduct[];
    three_day_certified_date?: string;
  };
}

const columns: ColumnDef<User>[] = [
  {
    key: 'user',
    header: 'User',
    render: (item) => (
      <div>
        <div className="font-medium">
          {item.attributes.first_name} {item.attributes.last_name}
        </div>
        <div className="text-muted-foreground text-xs">
          {item.attributes.email}
        </div>
      </div>
    ),
  },
  {
    key: 'organization',
    header: 'Organization',
    render: (item) =>
      item.attributes.account_name || item.attributes.affiliate_name || 'Direct',
  },
  {
    key: 'products',
    header: 'Tools',
    render: (item) => (
      <div className="flex flex-wrap gap-1">
        {item.attributes.products?.map((product) => (
          <span
            key={product.id}
            className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium bg-primary-50 text-primary"
          >
            {product.name}
            {product.expiration_date && (
              <span className="text-neutral-500">
                (exp: {new Date(product.expiration_date).toLocaleDateString()})
              </span>
            )}
          </span>
        ))}
      </div>
    ),
  },
  {
    key: 'three_day_certified_date',
    header: '3-Day Certified',
    render: (item) =>
      item.attributes.three_day_certified_date
        ? new Date(item.attributes.three_day_certified_date).toLocaleDateString()
        : '-',
  },
];

const filterConfig: FilterConfig = {
  fields: [
    { key: 'affiliate_id', type: 'affiliate-select', label: 'Affiliate', placeholder: 'All Affiliates', showFor: ['super', 'admin', 'affiliate_admin', 'data'] },
    { key: 'license_admin_id', type: 'organization-select', label: 'License Holder', placeholder: 'All License Holders', showFor: ['super', 'admin', 'affiliate_admin', 'data'] },
    { key: 'email_domain', type: 'text', label: 'Email Domain', placeholder: 'e.g., prosci.com', showFor: ['super', 'admin', 'affiliate_admin', 'data'] },
    { key: 'product_id', type: 'product-select', label: 'Tool/Product', placeholder: 'All Tools', showFor: ['super', 'admin', 'affiliate_admin', 'data', 'license_admin'] },
  ],
  hasFiltersCheck: (filters: ReportFilters) =>
    Boolean(
      filters.email_domain ||
      filters.affiliate_id ||
      filters.license_admin_id ||
      filters.product_id
    ),
};

export function UserListByToolReport() {
  return (
    <ReportPageLayout<User>
      title="User List by Tool Report"
      description="View users and their assigned tools and products."
      breadcrumbLabel="User List by Tool"
      itemLabel="users"
      queryKey="user-list-by-tool"
      fetchData={reportsService.getUserListByTool}
      downloadData={reportsService.downloadUserListByTool}
      filterConfig={filterConfig}
      columns={columns}
      sortData={(data) =>
        [...data].sort((a, b) => {
          const lastNameA = (a.attributes.last_name || '').toLowerCase();
          const lastNameB = (b.attributes.last_name || '').toLowerCase();
          if (lastNameA !== lastNameB) {
            return lastNameA.localeCompare(lastNameB);
          }
          const firstNameA = (a.attributes.first_name || '').toLowerCase();
          const firstNameB = (b.attributes.first_name || '').toLowerCase();
          return firstNameA.localeCompare(firstNameB);
        })
      }
      getRowKey={(item) => item.id}
      emptyIcon={<UserRoundX />}
      emptyTitle="No users found"
      emptyDescription="Try adjusting your filters to find users."
    />
  );
}
